import React, { FormEvent, useMemo, useState } from "react";
import {
  Dropdown,
  Option,
  Button,
  Text,
  makeStyles,
  tokens,
  Field,
} from "@fluentui/react-components";
import { Company } from "../../LogIn/useLogIn";
import {
  BaseCompanyFormParamsKeys,
  CompanyFormParams,
} from "../../../types/login";
import { ValidationErrors } from "../../../types/common";
import { createCompanyFormValidation } from "./CompanyForm.validator";
import { validateField, validateForm } from "../Form.validators";
import { ValidatableValue } from "../Form.types";

/* global HTMLFormElement */

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
    padding: "12px",
  },
  title: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightSemibold,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase300,
  },
});

interface CompanySelectProps {
  companies: Company[];
  error?: string;
  saving?: boolean;
  onClearError: () => void;
  onSubmit: (params: CompanyFormParams) => void;
}

type FormRecord = Record<keyof CompanyFormParams, ValidatableValue>;

const CompanyForm: React.FC<CompanySelectProps> = ({
  companies,
  error,
  saving,
  onClearError,
  onSubmit,
}) => {
  const styles = useStyles();
  const [formData, setFormData] = useState<CompanyFormParams>({
    company: "",
  });
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {},
  );

  const validationConfig = useMemo(() => createCompanyFormValidation(), []);

  const handleChange = (field: BaseCompanyFormParamsKeys, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (error) onClearError();

    const fieldConfig = validationConfig[field];
    if (fieldConfig) {
      const result = validateField<string>(value, fieldConfig);
      if (!result.isValid) {
        setValidationErrors((prev) => ({
          ...prev,
          [field]: result.error,
        }));
      } else {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors = validateForm<FormRecord>(
      formData as FormRecord,
      validationConfig,
    );
    setValidationErrors(errors);

    if (Object.keys(errors).length === 0 && !saving) {
      onSubmit(formData);
    }
  };

  const isSaveDisabled = Object.keys(validationErrors).length > 0;

  return (
    <form className={styles.container} onSubmit={handleSubmit}>
      <Text className={styles.title}>Select Company</Text>
      <Field
        id="company"
        label="Company"
        validationState={validationErrors.company ? "error" : "none"}
        validationMessage={validationErrors.company}
      >
        <Dropdown
          placeholder="Select your company"
          selectedOptions={formData.company ? [formData.company] : []}
          value={
            companies.find((company) => company.companyId === formData.company)
              ?.companyName || ""
          }
          onOptionSelect={(_, data) =>
            handleChange("company", data.optionValue || "")
          }
        >
          {companies.map((company) => (
            <Option key={company.companyId} value={company.companyId}>
              {company.companyName}
            </Option>
          ))}
        </Dropdown>
      </Field>

      {error && (
        <Field
          id="apiErrorMessage"
          validationState="error"
          validationMessage={error}
        />
      )}

      <Button
        appearance="primary"
        type="submit"
        disabled={!!error || isSaveDisabled || saving}
      >
        Continue
      </Button>
    </form>
  );
};

export default CompanyForm;
